import React, {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from 'react'
import { Formik } from 'formik'
import { useDispatch, useSelector } from 'react-redux'
import { Form, Spinner } from '@vwfs-bronson/bronson-react'
import getInitialValues from './initial-values'
import { RenderFieldset } from './fieldset'
import { getValidationSchema } from './validation-schema'
import { getDataModels } from '../../../services/redux/features/data-models.redux'
import {
  setTlsValid,
} from '../../../services/redux/features/custom.redux'
import { trackPage } from '../tracking'
import { getPartnerType } from '../../../services/common/utils'
import { PartnerType } from '../../../models/enums'

interface PersonalDetailsProps {
  hidden?: boolean
  editPage?: boolean
  isBusiness?: boolean
  isVtiGis?: boolean
  setIsDirty: (isDirty: boolean) => void
  getValues: (values: any) => void
}

const PersonalDetails = forwardRef((props: PersonalDetailsProps, ref) => {
  const {
    hidden,
    editPage,
    isBusiness,
    isVtiGis,
    setIsDirty: setIsDirtyParent,
    getValues,
  } = props

  const dispatch = useDispatch()

  const dataModels = useSelector(getDataModels)
  const formState = useRef({
    values: {} as any,
    isValid: false,
    dirty: false,
    validateForm: () => {},
  })
  const submitFunctionRef = useRef<() => void>()

  const [isDirty, setIsDirty] = useState(false)
  const partnerType = getPartnerType()

  const lastCheckedEmailRef = useRef<string | null>(null)
  const setLastCheckedEmail = (email: string | null) => {
    lastCheckedEmailRef.current = email
  }
  const getLastCheckedEmail = () => lastCheckedEmailRef.current

  const lastTlsRef = useRef<{ email: string | null; result: boolean | null }>({
    email: null,
    result: null,
  })
  const setLastTls = (email: string | null, result: boolean | null) => {
    lastTlsRef.current = { email, result }
  }
  const getLastTls = () => lastTlsRef.current

  const validationSchema = useCallback(
    (isBusiness, isVtiGis, partnerType) =>
      getValidationSchema(isBusiness, isVtiGis, partnerType, {
        onTlsEvaluated: (result: boolean) => {
          dispatch(setTlsValid(result))
        },
        getLastCheckedEmail,
        setLastCheckedEmail,
        getLastTls,
        setLastTls,
      }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [isBusiness, isVtiGis, partnerType]
  )

  const initialValid = useMemo(() => {
    const schema = validationSchema(isBusiness, isVtiGis, partnerType)
    return schema.isValidSync(getInitialValues()) ?? false
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isBusiness, isVtiGis])

  useImperativeHandle(ref, () => ({
    submit: async () => {
      setLastCheckedEmail(null)
      await formState.current.validateForm?.()
      await submitFunctionRef.current?.()
    },
    getDkxNumber: () => formState?.current?.values?.dkxNumber,
    getCustomerData: () => {
      const { dateOfBirth, ...rest } = formState.current.values.customerData
      // eslint-disable-next-line no-nested-ternary
      return partnerType === PartnerType.BUSINESS
        ? { ...rest, ...(dateOfBirth && { dateOfBirth }) }
        : isVtiGis
          ? formState.current.values.customerData
          : {
              ...formState.current.values.customerData,
              address: {
                ...formState.current.values.customerData.address,
                countryCode: undefined,
              },
            }
    },
    getCompanyData: () => formState.current.values.companyData,
    isValid: () => formState.current.isValid,
    validate: () => formState.current.isValid,
  }))

  useEffect(() => {
    if (!hidden) {
      trackPage({ firstOpen: !editPage })
    }
  }, [hidden, editPage])

  useEffect(() => {
    setIsDirtyParent(isDirty)
  }, [isDirty, setIsDirtyParent])

  useEffect(() => {
    setLastCheckedEmail(null)
    formState.current?.validateForm?.()
  }, [partnerType])

  return (
    <>
      {Object.keys(dataModels).length ? (
        <Formik
          enableReinitialize
          initialValues={getInitialValues()}
          validationSchema={validationSchema(isBusiness, isVtiGis, partnerType)}
          isInitialValid={initialValid}
          validateOnBlur
          validateOnChange={false}
          onSubmit={() => {}}
        >
          {({
            values,
            handleSubmit,
            handleBlur,
            submitForm,
            setFieldValue,
            isValid,
            dirty,
            validateForm,
          }) => {
            getValues(values)
            formState.current = {
              values,
              isValid,
              dirty: dirty,
              validateForm,
            }
            setIsDirty(dirty)
            submitFunctionRef.current = submitForm

            const handleSubmitOwn = (e) => {
              setLastCheckedEmail(null)
              handleSubmit(e)
            }

            const handleBlurOwn = (e) => {
              handleBlur(e)
            }
            const handleChangeOwn = (ev) => {
              if (
                ev.target.name === 'customerData.contactData.mobilePhoneNumber'
              ) {
                setFieldValue(
                  'customerData.contactData.mobilePhoneNumber',
                  ev.target.value.replace(/\s+/g, '')
                )
              }
              if (ev.target.name === 'customerData.contactData.email') {
                setFieldValue(
                  'customerData.contactData.email',
                  ev.target.value.replace(/\s+/g, '')
                )
              }
            }

            return (
              <Form
                floatingLabel
                onSubmit={handleSubmitOwn}
                onBlur={handleBlurOwn}
                onChange={handleChangeOwn}
              >
                <RenderFieldset isBusiness={isBusiness} isVtiGis={isVtiGis} />
              </Form>
            )
          }}
        </Formik>
      ) : (
        <div className="u-p-xlarge">
          <Spinner section />
        </div>
      )}
    </>
  )
})

export default PersonalDetails
